import "./styles.scss";

import React, { useState, useEffect, useMemo } from "react";
import { format, fromUnixTime } from "date-fns";
import { BsDatabaseFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AiFillPauseCircle, AiFillPlayCircle } from "react-icons/ai";
import { Checkbox } from "@nextui-org/react";
import { useParams } from "react-router-dom";
import { MdOutlinePhoneCallback, MdOutlinePhoneForwarded } from "react-icons/md";
import { IoCloseSharp } from "react-icons/io5";

import LiveContentModalContent from "./LiveContentModalContent";
import LiveContentSearchbar from "./LiveContentSearchbar";
import PaginationTable from "../../components/PaginationTable";
import Modal from "../../components/Modal";
import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";

import { live_content_search_table_columns } from "../../constants/tableHeaders";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import { useAppContext } from "../../contexts/AppContext";
import {
  addSelectedCall,
  addSelectedCalls,
  getCallsInAlgolia,
  fetchDeepLinkCallInAlgolia,
  removeSelectedCall,
  removeSelectedCalls,
} from "../../redux/actions";
import { getAlgoliaInfos } from "../../redux/actions/algoliaActions";
import { ArrowTopRightIcon } from "../../assets/icons/audio-icons";
import getConfig from "../../conf/config";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import {
  CALL_DURATION_FILTER_TYPE,
  formatCallDurationRange,
  getCallDurationRange,
  withCallDurationRange,
} from "../../components/CallDurationDropdown";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { formatCallDuration } from "../../utils/callsUtils";
import { auth } from "../../conf/firebase";
import myAxios from "../../utils/axiosWrapper";

const LiveContentSearch = () => {
  const { t } = useTranslation();
  let { callId } = useParams();
  const { currentUser } = auth;
  const { user } = useSelector((state) => state.user);
  const [hasOpenedDeepLink, setHasOpenedDeepLink] = useState(false);

  const {
    debouncedSearchText: searchText,
    callsDateRange,
    teamGrades,
    individualGrades,
    audioUrl,
    sidebarAudioUrl,
    setSidebarAudioUrl,
    setSidebarCallId,
    sidebarIsAudioPlaying,
    setSidebarIsAudioPlaying,
    setSidebarAudioTime,
    sidebarAudioRef,
    setSidebarAutoPlay,
    setPopupAudioUrl,
    setPopupCallId,
    setPopupAudioTime,
    setPopupIsAudioPlaying,
    popupIsAudioPlaying,
    popupAudioTime,
    popupAudioSpeed,
    setSidebarAudioSpeed,
    handleOnGradeChange,
  } = useAppContext();
  const {
    calls,
    loading,
    selectedCalls = [],
    deepLinkCall = {},
  } = useSelector((state) => state.calls.calls);
  const selectedcallsIds = selectedCalls?.map((call) => call.id) || [];
  const { company_config, loading: algoliaLoading } = useSelector((state) => state.algolia.algolia);

  const display_tz = company_config?.display_tz;

  const [gradeValue, setGradeValue] = useState([0, 10]);
  const [pageFilters, setPageFilters] = useState([]);
  const callDurationRange = getCallDurationRange(pageFilters);

  const setCallDurationRange = (range) => {
    setPageFilters(withCallDurationRange(pageFilters, range));
  };
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "call_timestamp",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [currentCalls, setCurrentCalls] = useState([]);

  useEffect(() => {
    setCurrentCalls(calls);
  }, [calls]);

  const dispatch = useDispatch();

  const fetchDeepLinkCall = () => {
    if (!callId) {
      return;
    }

    dispatch(fetchDeepLinkCallInAlgolia(callId));
  };

  useEffect(() => {
    if (!callId) {
      return;
    }

    if (hasOpenedDeepLink) {
      return;
    }

    fetchDeepLinkCall();
  }, [callId, dispatch, algoliaLoading, hasOpenedDeepLink]);

  const onOpenCall = (item) => {
    if (item && item?.analysis) {
      setSelectedItem(item);
      setPopupAudioUrl(item.audio_url);
      setPopupCallId(item.id);
      setPopupAudioTime(0);
      setSidebarAutoPlay(false);
      setIsModalOpen(true);
    }
  };

  const [organisations, setOrganisations] = useState({});

  const fetchCompanyOrganisations = async () => {
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/company_organisations`
    );
    const retrievedOrganisations = response?.data?.[0] || {};

    setOrganisations(retrievedOrganisations);
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    fetchCompanyOrganisations();
  }, [user]);

  const [operators, setOperators] = useState([]);

  const fetchCompanyOperators = async () => {
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/simple_operators`
    );
    const retrievedOperators = response?.data?.[0]?.operators || [];

    setOperators(retrievedOperators);
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    fetchCompanyOperators();
  }, [user]);

  const handleOnRowAction = (item) => () => {
    onOpenCall(item);
  };

  useEffect(() => {
    if (!callId || !deepLinkCall?.id) {
      return;
    }

    if (callId !== deepLinkCall?.id) {
      return;
    }

    if (hasOpenedDeepLink) {
      return;
    }

    setHasOpenedDeepLink(true);
    onOpenCall(deepLinkCall);
  }, [deepLinkCall, callId, hasOpenedDeepLink]);

  useEffect(() => {
    dispatch(getAlgoliaInfos());
  }, [dispatch]);

  const fetchCalls = () => {
    const isLiveContentSearch = true;

    dispatch(
      getCallsInAlgolia(
        searchText,
        {
          start_date: callsDateRange.startDate,
          end_date: callsDateRange.endDate,
          ...teamGrades,
          ...individualGrades,
        },
        page - 1,
        rowsPerPage,
        sortDescriptor,
        isLiveContentSearch,
        pageFilters,
        display_tz
      )
    );
  };

  useEffect(() => {
    fetchCalls();
  }, [
    individualGrades,
    callsDateRange,
    sortDescriptor,
    searchText,
    dispatch,
    algoliaLoading,
    page,
    rowsPerPage,
    pageFilters,
  ]);

  const renderTableEmptyState = () => {
    return (
      <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
        <BsDatabaseFill className="text-gray-300 w-12 h-12" />
        <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
      </div>
    );
  };

  // Transcripts are SRT: keep only the spoken text. Drops the cue numbers, the
  // timecode lines and the "<speaker>: " prefixes, but not the digits inside the
  // text (numbers are transcribed as digits).
  const cleanTranscript = (transcript) => {
    if (!transcript) return "";
    return transcript
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !/^\d+$/.test(line) && !line.includes("-->"))
      .map((line) => line.replace(/^\d+:\s*/, ""))
      .join(" ");
  };

  const onCloseCallModal = () => {
    setIsModalOpen(false);
    if (popupIsAudioPlaying) {
      setPopupIsAudioPlaying(false);

      setSidebarIsAudioPlaying(true);
      setSidebarAudioTime(popupAudioTime);
      setSidebarAudioSpeed(popupAudioSpeed);
      if (sidebarAudioRef && sidebarAudioRef.current) {
        sidebarAudioRef.current.currentTime = popupAudioTime;
        sidebarAudioRef.current.playbackRate = popupAudioSpeed;
        sidebarAudioRef.current.play();
      }
    }
  };

  const onPlayPauseClick = (item) => (e) => {
    e.stopPropagation();

    // const item = getCallFromId(callId);
    if (!item || !item.audio_url) {
      return;
    }

    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl;
    if (!sidebarAudioRef.current) {
      return;
    }

    setSidebarAutoPlay(true);

    if (isCurrentlyPlaying) {
      if (sidebarIsAudioPlaying) {
        sidebarAudioRef.current.pause();
        setSidebarIsAudioPlaying(false);
      } else {
        sidebarAudioRef.current.play();
        setSidebarIsAudioPlaying(true);
      }
      return;
    }

    setSidebarAudioUrl(item.audio_url);
    setSidebarCallId(item.id);
    setSidebarAudioTime(0);
    sidebarAudioRef.current.currentTime = 0;
  };

  const onAddSelection = (item) => () => {
    dispatch(addSelectedCall(item));
  };

  const onRemoveSelection = (item) => () => {
    dispatch(removeSelectedCall(item));
  };

  const getIsSelectAllChecked = () => {
    let hasNewCalls = false;
    let cpt = 0;

    while (!hasNewCalls && cpt < currentCalls.length) {
      const item = currentCalls[cpt];
      if (!selectedcallsIds.includes(item.id)) {
        hasNewCalls = true;
      }
      cpt++;
    }

    return !hasNewCalls;
  };

  const onSelectAll = () => {
    const isAllSelected = getIsSelectAllChecked();
    dispatch(isAllSelected ? removeSelectedCalls(currentCalls) : addSelectedCalls(currentCalls));
  };

  const memoizedSearchResults = useMemo(() => {
    const loweredSearchText = searchText.toLowerCase();
    const TRANSCRIPT_SLICE_SIZE = 60;
    const TRANSCRIPT_SLICE_OFFSET = 18;

    return currentCalls.reduce((accumulator, call) => {
      const loweredCleanedTranscript = cleanTranscript(call?.transcript)?.toLowerCase();

      const indexOfFirstMatch = loweredCleanedTranscript.indexOf(loweredSearchText);

      if (indexOfFirstMatch === -1) {
        accumulator[call.id] = loweredCleanedTranscript.substring(0, TRANSCRIPT_SLICE_SIZE);
        return accumulator;
      }

      const toDisplay = loweredCleanedTranscript
        .substring(
          indexOfFirstMatch - TRANSCRIPT_SLICE_OFFSET,
          indexOfFirstMatch + TRANSCRIPT_SLICE_SIZE - TRANSCRIPT_SLICE_OFFSET
        )
        .replace(loweredSearchText, `<em>${searchText}</em>`);

      accumulator[call.id] = toDisplay;

      return accumulator;
    }, {});
  }, [currentCalls, searchText]);

  const renderCell = (item, columnKey) => {
    const cellValue = item[columnKey];
    let { id: itemId } = item;
    let displayableId = itemId ? `...${itemId.substring(itemId.length - 6)}` : "";

    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl && sidebarIsAudioPlaying;
    const isSelected = selectedcallsIds.includes(itemId);

    switch (columnKey) {
      case "checkbox":
        return (
          <Checkbox
            isSelected={isSelected}
            onValueChange={isSelected ? onRemoveSelection(item) : onAddSelection(item)}
          />
        );
      case "play_button":
        return (
          <div className="flex items-center space">
            <div
              className="w-auto h-auto flex items-center justify-center"
              onClick={onPlayPauseClick(item)}
              onKeyDown={onPlayPauseClick(item)}
            >
              {isCurrentlyPlaying && sidebarIsAudioPlaying ? (
                <AiFillPauseCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              ) : (
                <AiFillPlayCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              )}
            </div>
          </div>
        );
      case "call_id":
        return (
          <div className="text-sm font-normal whitespace-normal">
            <CustomCopyToClipboard
              displayClassName="text-[#171717] min-w-[80px]"
              copyText={itemId}
              displayText={displayableId}
            />
          </div>
        );
      case "operator_name":
        return (
          <ACircleUserDisplay
            firstname={item.operator_name
              .split(" ")
              .map((item) => capitalizeFirstLetter(item))
              .join(" ")}
            lastname=""
            email=""
          />
        );
      case "call_timestamp":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal flex flex-col space-y-1">
            <span>{format(fromUnixTime(item.call_timestamp), "MMM dd, yyyy")}</span>
          </div>
        );
      case "duration":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal tabular-nums">
            {formatCallDuration(item?.call_duration_seconds)}
          </div>
        );
      case "client_number":
        return (
          <div className="text-sm text-[#737373] font-normal flex items-center">
            <div>
              {item.incoming ? (
                <MdOutlinePhoneCallback
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              ) : (
                <MdOutlinePhoneForwarded
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              )}
            </div>
            {!item?.client_number || item.client_number === "None" ? (
              t("Unknown")
            ) : (
              <CustomCopyToClipboard
                wrapperClassName="!p-1 -ml-1"
                displayClassName="!pr-1.5"
                copyText={item.client_number}
                displayText={item.client_number}
              />
            )}
          </div>
        );
      case "result":
        return (
          <div className="live-content-search__result-wrapper text-sm font-normal relative overflow-hidden px-[8px] py-[4px]">
            {memoizedSearchResults[item?.id]?.length > 0 && (
              <>
                <div className="live-content-search__result-shadow live-content-search__result-shadow--start" />
                <div className="live-content-search__result-shadow live-content-search__result-shadow--end" />
              </>
            )}
            <div
              className="live-content-search__result"
              dangerouslySetInnerHTML={{ __html: memoizedSearchResults[item?.id] }}
            />
          </div>
        );
      default:
        return (
          <div className="w-full text-sm text-[#404040]/[0.6] float-left pr-3 whitespace-normal">
            {cellValue}
          </div>
        );
    }
  };

  const onTogglePageFilter = (filter, valueToFilter) => {
    const currentlySelectedFilters = pageFilters.map((item) => item?.type);
    const isCurrentlySelected = currentlySelectedFilters.includes(filter);

    console.log({ filter, valueToFilter });

    let newPageFilters = [];

    if (!isCurrentlySelected) {
      newPageFilters = [...pageFilters, { type: filter, valueToFilter }];
    } else {
      newPageFilters = [...pageFilters].filter((item) => item.type !== filter);
    }

    setPageFilters(newPageFilters);
  };

  const setChoiceFilterParam = (filterKey) => (param) => {
    const choicePageFilterIndex = pageFilters.findIndex((item) => item.type === filterKey);

    if (choicePageFilterIndex === -1) {
      const newPageFilters = [...pageFilters];
      newPageFilters.push({ type: filterKey, params: [param] });
      setPageFilters(newPageFilters);

      return;
    }
    const currentlySelectedParams = pageFilters[choicePageFilterIndex]?.params || [];
    const isParamCurrentlySelected = currentlySelectedParams.includes(param);

    let newParams = [...currentlySelectedParams];

    if (!isParamCurrentlySelected) {
      newParams = [...newParams, param];
    } else {
      newParams = [...currentlySelectedParams].filter((item) => item !== param);
    }

    const newPageFilters = [...pageFilters];

    newPageFilters[choicePageFilterIndex].params = newParams;
    setPageFilters(newPageFilters);
  };

  const renderDefaultPageFilter = () => {
    const filterToLabelMap = {
      department: "Department",
      operators: "Opérateur",
    };

    const filterParamFormat = (type, param) => {
      if (type === "department") {
        return organisations?.departments?.find((item) => item.id === param)?.name || "-";
      }
      if (type === "operators") {
        const selectedOperator = operators?.find((item) => item.id === param);
        return selectedOperator
          ? `${selectedOperator.first_name || ""} ${selectedOperator.last_name || ""}`.trim()
          : "-";
      }

      return param;
    };

    return (
      <div className="flex gap-[8px] justify-between items-start">
        <div className="flex items-center gap-[8px] flex-wrap">
          {/* Date filter */}
          <div className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary">
            <span className="whitespace-nowrap">
              {`${format(callsDateRange.startDate, "dd/MM/yyyy")} - ${format(
                callsDateRange.endDate,
                "dd/MM/yyyy"
              )}`}
            </span>
          </div>
          {/* grade filter */}
          {(gradeValue[0] !== 0 || gradeValue[1] !== 10 || individualGrades?.exclude_na) && (
            <div
              onClick={() => {
                setGradeValue([0, 10]);
                handleOnGradeChange("individual", {
                  min_individual_grade: 0,
                  max_individual_grade: 10,
                  exclude_na: false,
                });
              }}
              onKeyDown={() => {
                setGradeValue([0, 10]);
                handleOnGradeChange("individual", {
                  min_individual_grade: 0,
                  max_individual_grade: 10,
                  exclude_na: false,
                });
              }}
              className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
            >
              <span className="whitespace-nowrap">
                {`Grade : ${Math.round(gradeValue[0])} - ${Math.round(gradeValue[1])}${
                  individualGrades?.exclude_na ? " (no N.A)" : ""
                }`}
              </span>
              <IoCloseSharp size={18} />
            </div>
          )}
          {/* Multiple choices filters */}
          {pageFilters.map((pageFilter) => {
            if (pageFilter.type === CALL_DURATION_FILTER_TYPE) {
              return (
                <div
                  key={pageFilter.type}
                  onClick={() => setCallDurationRange([null, null])}
                  onKeyDown={() => setCallDurationRange([null, null])}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${t("Call duration")} : ${formatCallDurationRange(callDurationRange)}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            }

            return pageFilter?.params?.map((filterParam) => {
              return (
                <div
                  key={`${pageFilter.type}-${filterParam}`}
                  onClick={() => setChoiceFilterParam(pageFilter.type)(filterParam)}
                  onKeyDown={() => setChoiceFilterParam(pageFilter.type)(filterParam)}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${t(filterToLabelMap[pageFilter.type])} : ${filterParamFormat(
                      pageFilter.type,
                      filterParam
                    )}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            });
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 pb-32 max-w-full">
      <LiveContentSearchbar
        onTogglePageFilter={onTogglePageFilter}
        setChoiceFilterParam={setChoiceFilterParam}
        pageFilters={pageFilters}
        organisations={organisations}
        operators={operators}
        company_config={company_config}
        callDurationRange={callDurationRange}
        setCallDurationRange={setCallDurationRange}
        gradeValue={gradeValue}
        setGradeValue={setGradeValue}
      />
      <div className="mt-[14px]">{renderDefaultPageFilter()}</div>
      <PaginationTable
        items={currentCalls}
        selectAllAction={onSelectAll}
        isSelectAllChecked={getIsSelectAllChecked()}
        columns={live_content_search_table_columns.map((column) => ({
          ...column,
          label: column.label ? t(column.label) : column.label,
        }))}
        emptyState={renderTableEmptyState()}
        renderCell={renderCell}
        onRowAction={handleOnRowAction}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        isLoading={loading}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        overrideNbPageOptions={[12, 25]}
      />
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          showFooter={false}
          size="4xl"
          handleOnClose={onCloseCallModal}
        >
          <LiveContentModalContent
            item={selectedItem}
            handleOnClose={onCloseCallModal}
          />
        </Modal>
      )}
    </div>
  );
};

export default LiveContentSearch;
